import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'football');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.categorySlug === activeSlug);

  const handleCategorySwitch = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticleClick = (id: string) => {
    navigate(`/article/${id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categoryJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCategory.name} - kaiyun体育`,
    "description": currentCategory.description,
    "url": `https://yunmatch.github.io/category/${activeSlug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "kaiyun体育",
      "url": "https://yunmatch.github.io/"
    }
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://yunmatch.github.io/" },
      { "@type": "ListItem", "position": 2, "name": currentCategory.name, "item": `https://yunmatch.github.io/category/${activeSlug}` }
    ]
  };

  return (
    <>
      <title>{currentCategory.name} - kaiyun体育 | 全球{currentCategory.name}赛事资讯</title>
      <meta name="description" content={currentCategory.description} />
      <meta name="keywords" content={`kaiyun体育,kaiyun体育${currentCategory.name},{currentCategory.name}赛事,${currentCategory.name}直播,${currentCategory.name}资讯`} />
      <link rel="canonical" href={`https://yunmatch.github.io/category/${activeSlug}`} />
      <meta property="og:title" content={`${currentCategory.name} - kaiyun体育 | 全球${currentCategory.name}赛事资讯`} />
      <meta property="og:description" content={currentCategory.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://yunmatch.github.io/category/${activeSlug}`} />
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <Navbar />
      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />

            <div className="py-6 md:py-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-3xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                    {currentCategory.name}
                  </h1>
                  <p className="text-foreground-600 text-sm md:text-base mt-1">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySwitch(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredArticles.map((article) => (
                  <article
                    key={article.id}
                    onClick={() => handleArticleClick(article.id)}
                    className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:bg-background-200/70 border border-transparent hover:border-background-300/60"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-xs line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {article.author}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}